import { useMemo, useState, type ReactNode } from 'react';
import { applyMode, applyDensity, Mode, Density } from '@cloudscape-design/global-styles';
import {
  PreferencesContext,
  DEFAULT_PREFERENCES,
  type Preferences,
} from '@deloitte-us-consulting/gp-sdk';

export const PREFERENCES_STORAGE_KEY = 'gp.preferences.v1';

/** The saved color mode and density, each kept only if it's a value this build knows. */
function readSaved(): Partial<Preferences> {
  try {
    const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
    const saved: unknown = raw ? JSON.parse(raw) : null;
    if (!saved || typeof saved !== 'object') return {};
    const { colorMode, density } = saved as Record<string, unknown>;
    return {
      ...(colorMode === 'light' || colorMode === 'dark' ? { colorMode } : {}),
      ...(density === 'comfortable' || density === 'compact' ? { density } : {}),
    };
  } catch {
    return {};
  }
}

function save({ colorMode, density }: Preferences): void {
  try {
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify({ colorMode, density }));
  } catch {
    // Private mode or a full quota: the choice still applies, it just isn't remembered.
  }
}

function apply({ colorMode, density }: Partial<Preferences>): void {
  if (colorMode) applyMode(colorMode === 'dark' ? Mode.Dark : Mode.Light);
  if (density) applyDensity(density === 'compact' ? Density.Compact : Density.Comfortable);
}

/**
 * Shell-owned user preferences. Applies Cloudscape color mode + density globally and
 * provides the SDK PreferencesContext so remotes read/write via usePreferences().
 * Color mode and density are remembered in this browser (localStorage), so the
 * top-bar theme toggle survives a reload or a new tab.
 * TODO(platform): persist to the platform user-settings store so it follows the user.
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setState] = useState<Preferences>(() => {
    const initial = { ...DEFAULT_PREFERENCES, ...readSaved() };
    apply(initial);
    return initial;
  });

  const value = useMemo(
    () => ({
      preferences,
      setPreferences: (p: Partial<Preferences>) =>
        setState((prev) => {
          const next = { ...prev, ...p };
          apply(p);
          save(next);
          return next;
        }),
    }),
    [preferences],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}
